import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithBackground.js";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { FiShield, FiCalendar, FiTrendingUp, FiClock, FiBarChart2, FiLink } from "react-icons/fi";

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;
const ContentWithPaddingXl = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24 px-4 sm:px-6 lg:px-8`;
const ContentWithVerticalPadding = tw.div`py-20 lg:py-24`;
const Content2Xl = tw.div`max-w-screen-2xl mx-auto`;

const Heading = tw.h1`font-bold text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-gray-900 leading-tight`;
const HeadingRow = tw.div`flex flex-col items-center text-center`;
const Text = tw.div`mt-8 text-base text-gray-600 max-w-3xl mx-auto`;

const FeatureGrid = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12`;
const Feature = tw.div`bg-white rounded-lg shadow-lg p-8`;
const FeatureIcon = tw.div`text-primary-500 text-4xl mb-4`;
const FeatureTitle = tw.h3`text-xl font-semibold text-gray-900 mb-2`;
const FeatureDescription = tw.p`text-gray-600`;

const SolutionSection = tw.div`mt-16`;
const SolutionGrid = tw.div`grid grid-cols-1 md:grid-cols-2 gap-8 mt-8`;
const SolutionCard = tw.div`bg-white rounded-lg shadow-lg p-8`;
const SolutionTitle = tw.h3`text-xl font-semibold text-gray-900 mb-2`;
const SolutionDescription = tw.p`text-gray-600 mb-4`;
const SolutionFeatures = tw.ul`space-y-2 mb-6`;
const SolutionFeature = tw.li`flex items-center text-gray-600`;
const SolutionButton = tw.button`w-full py-3 bg-primary-500 text-white rounded-lg font-medium tracking-wide text-sm transition duration-300 transform focus:outline-none focus:shadow-outline hover:bg-primary-600 hover:shadow-lg`;

export default () => {
  const features = [
    {
      icon: <FiShield />,
      title: "Sécurité Renforcée",
      description: "Contrôlez et tracez chaque visiteur grâce à l'identification numérique et au suivi en temps réel."
    },
    {
      icon: <FiCalendar />,
      title: "Gestion des Rendez-vous",
      description: "Planifiez et gérez facilement les visites, réduisez les échanges et confirmations manuels."
    },
    {
      icon: <FiTrendingUp />,
      title: "Entrée Accélérée",
      description: "Pré-inscrivez vos visiteurs pour des entrées plus rapides et plus fluides. QR code ou numéro de téléphone à l'entrée."
    },
    {
      icon: <FiClock />,
      title: "Suivi en Temps Réel",
      description: "Visualisez en temps réel qui est présent sur site et les horaires d'entrée/sortie."
    },
    {
      icon: <FiBarChart2 />,
      title: "Rapports Détaillés",
      description: "Générez des rapports sur le flux de visiteurs, les tendances d'accès et les indicateurs clés."
    },
    {
      icon: <FiLink />,
      title: "Intégration Facile",
      description: "Connectez Tanga à vos systèmes existants pour une gestion centralisée."
    }
  ];

  const solutions = [
    {
      title: "PMEs, Hopitaux, Bureaux, etc.",
      description: "Optimisez la gestion des visiteurs, améliorez la sécurité et offrez une expérience moderne à vos invités.",
      features: [
        "Prise de rendez-vous en ligne",
        "Check-in rapide avec QR code",
        "Suivi des visiteurs en temps réel",
        "Rapports d'activité",
        "Support dédié"
      ]
    },
    {
      title: "Grandes Entreprises & Sites Sensibles",
      description: "Bénéficiez d'une traçabilité avancée, d'une gestion multi-sites et d'une sécurité renforcée.",
      features: [
        "Gestion multi-sites et accès différenciés",
        "Contrôle d'accès avancé",
        "Rapports personnalisés",
        "Intégration avec systèmes de sécurité",
        "Support 24/7"
      ]
    }
  ];

  return (
    <AnimationRevealPage>
      <Header />
      <Container>
        <ContentWithPaddingXl>
          <HeadingRow>
            <Heading>Gestion des Visiteurs pour Entreprises</Heading>
          </HeadingRow>
          <Text>
            <p className="text-center mb-8">
              Tanga digitalise et sécurise l'accueil de vos visiteurs. Gagnez en efficacité, améliorez la traçabilité et offrez une expérience d'accueil moderne et rapide à vos invités, tout en renforçant la sécurité de vos locaux.
            </p>
          </Text>

          <FeatureGrid>
            {features.map((feature, index) => (
              <Feature key={index}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </Feature>
            ))}
          </FeatureGrid>

          <SolutionSection>
            <HeadingRow>
              <Heading>Nos Solutions</Heading>
            </HeadingRow>
            <Text>
              <p className="text-center mb-8">
                Des solutions adaptées à la taille et aux besoins de votre entreprise.
              </p>
            </Text>

            <SolutionGrid>
              {solutions.map((solution, index) => (
                <SolutionCard key={index}>
                  <SolutionTitle>{solution.title}</SolutionTitle>
                  <SolutionDescription>{solution.description}</SolutionDescription>
                  <SolutionFeatures>
                    {solution.features.map((feature, featureIndex) => (
                      <SolutionFeature key={featureIndex}>
                        <span className="mr-2">✓</span>
                        {feature}
                      </SolutionFeature>
                    ))}
                  </SolutionFeatures>
                  <SolutionButton>Demander une démo</SolutionButton>
                </SolutionCard>
              ))}
            </SolutionGrid>
          </SolutionSection>
        </ContentWithPaddingXl>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
}; 